/**
 * SW2-2443 — how the offline project sync sends one queued write, and how it
 * tells "the server said no" from "the server could not be reached".
 *
 * Every request passes `{ replay: true }` so an unreachable server rejects here
 * rather than being queued again as a success (SW2-2321).
 */
import { isNetworkFailure } from './offlineFallback';

/** The request was answered, but not accepted. The item stays queued. */
export const FAILED = Symbol('offline-sync-failed');

/** The server could not be reached. The sync stops here. */
export class ConnectionLost extends Error {
  constructor(label, cause) {
    super(`Connection lost while syncing ${label}`);
    this.name = 'ConnectionLost';
    this.cause = cause;
  }
}

// `navigator.onLine` as well: some API wrappers refuse offline without making a
// request (uploadApi.create), so there is no axios error to classify.
const isUnreachable = (err) => isNetworkFailure(err) || !navigator.onLine;

/** Send one queued write: its result, or FAILED if the server refused it. */
export async function send(label, request) {
  try {
    return await request();
  } catch (err) {
    if (isUnreachable(err)) {
      throw new ConnectionLost(label, err);
    }

    console.error(`Offline sync: ${label} was not accepted`, err);

    return FAILED;
  }
}

/**
 * Run a step that handles server rejections itself (syncPendingFindingPhotos)
 * but must still stop the sync when the server is unreachable.
 */
export async function runStep(label, step) {
  try {
    return await step();
  } catch (err) {
    if (!(err instanceof ConnectionLost) && isUnreachable(err)) {
      throw new ConnectionLost(label, err);
    }

    throw err;
  }
}

export const isPending = (row) => Boolean(row?.sync) && row.sync.synced === false;

export const hasPendingCreate = (finding) =>
  isPending(finding) && finding.sync.payload?.[0]?.method === 'create';
